import React, { useEffect, useState } from "react";
import "./FeedbackForm.css";

const ENDPOINT = process.env.REACT_APP_RYD_FEEDBACK_ENDPOINT ?? "https://script.google.com/macros/s/AKfycby6Nwn_R7ZOf-OsGYtLLolNHIxEOCY1EWpmIcEu-YS6mnDVJ5L-Cmejhl6G-u2siVBt/exec";
const BRAND = process.env.REACT_APP_RYD_PRIMARY || "#ff6a00";
const LOGO = process.env.REACT_APP_RYD_LOGO || "/images/ryd-logo.svg";
const WA = process.env.REACT_APP_RYD_WHATSAPP || "";
const PHONE = process.env.REACT_APP_RYD_PHONE || "";
const EMAIL = (
  process.env.REACT_APP_RYD_EMAIL || "feedback@rydnepal.com"
).trim();

function getReferenceId() {
  const params = new URLSearchParams(window.location.search);
  return (params.get("referenceId") || "").trim();
}
function isValidRef(v) {
  return /^[A-Za-z0-9]{4}$/.test(String(v || "").trim());
}

export default function FeedbackForm() {
  const [referenceId, setReferenceId] = useState(getReferenceId());

  // Watch query string for changes (works in any SPA)
  useEffect(() => {
    let lastSearch = window.location.search;
    const timer = setInterval(() => {
      if (window.location.search !== lastSearch) {
        lastSearch = window.location.search;
        setReferenceId(getReferenceId());
      }
    }, 300);
    return () => clearInterval(timer);
  }, []);

  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("submitting");
    setError("");

    const formEl = e.currentTarget;

    const ref = (referenceId || "").trim();
    const message = (formEl.message?.value || "").trim();
    const name = (formEl.name?.value || "").trim();
    const phoneDigits = (formEl.phone?.value || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    const ratingVal = rating ? String(rating) : "";

    if (!isValidRef(ref)) {
      setStatus("error");
      setError("Please open via QR or add ?referenceId=AB12 to the URL.");
      return;
    }
    if (!message) {
      setStatus("error");
      setError("Feedback comment is required.");
      return;
    }
    if (!ENDPOINT) {
      setStatus("error");
      setError("Feedback endpoint is not configured.");
      return;
    }

    try {
      // Hidden-iframe navigation POST (bypasses CORS)
      const iframeName = "hidden_iframe_" + Date.now();
      const iframe = document.createElement("iframe");
      iframe.name = iframeName;
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      const f = document.createElement("form");
      f.method = "POST";
      f.action = ENDPOINT;
      f.target = iframeName;

      const add = (n, v) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = n;
        i.value = v ?? "";
        f.appendChild(i);
      };

      add("referenceId", ref);
      add("message", message);
      add("name", name);
      add("phone", phoneDigits);
      add("rating", ratingVal);

      document.body.appendChild(f);
      f.submit();

      setTimeout(() => {
        f.remove();
        iframe.remove();
      }, 1200);

      setStatus("success");
      formEl.reset();
      setRating(0);
      setHoverRating(0);
    } catch (err) {
      setStatus("error");
      setError(err?.message || "Something went wrong.");
    }
  }

  const current = hoverRating || rating;

  return (
    <div className="feedback-wrapper" style={{ "--ryd-primary": BRAND }}>
      <header className="feedback-header">
        {/* eslint-disable-next-line */}
        <img src={LOGO} alt="RYD Nepal" className="ryd-logo" />
        <h1>RYD Feedback – Share Your Ride Experience</h1>
        <p>Your voice helps us improve every ride.</p>
      </header>

      {!isValidRef(referenceId) && (
        <p className="hint">
          Tip: open this page via the helmet QR so the bike’s 4-character Ref ID
          is attached.
        </p>
      )}

      <form onSubmit={onSubmit} className="feedback-card">
        <input type="hidden" name="referenceId" value={referenceId} readOnly />

        <label className="field">
          <span className="label">
            Feedback <span className="req">*</span>
          </span>
          <textarea
            name="message"
            rows="5"
            required
            placeholder="Tell us what went well or what we can improve…"
          />
        </label>

        <div className="grid">
          <label className="field">
            <span className="label">Name</span>
            <input name="name" type="text" placeholder="Your name (optional)" />
          </label>

          <label className="field">
            <span className="label">Phone</span>
            <input
              name="phone"
              type="text"
              inputMode="tel"
              placeholder="98XXXXXXXX (optional)"
              maxLength={10}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
              }}
            />
          </label>
        </div>

        <div className="field">
          <span className="label">Rating</span>
          <div className="stars" role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`star ${current >= star ? "active" : ""}`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
              >
                ★
              </button>
            ))}
          </div>
          <input type="hidden" name="rating" value={rating} readOnly />
        </div>

        <button type="submit" className="submit-btn">
          {status === "submitting" ? "Submitting…" : "Submit"}
        </button>

        <p className={`status ${status}`}>
          {status === "success"
            ? "Thank you! Your feedback has been recorded."
            : error}
        </p>
      </form>

      <footer className="feedback-footer">
        <p>Need quick help? Reach RYD Nepal:</p>
        <div className="contact-links">
          <a
            href={`https://wa.me/${WA}`}
            target="_blank"
            rel="noreferrer"
            className="contact-link"
          >
            <span className="ico">💬</span> WhatsApp
          </a>
          <a href={`tel:${PHONE}`} className="contact-link">
            <span className="ico">📞</span> Call
          </a>
          <a href={`mailto:${EMAIL}`} className="contact-link">
            <span className="ico">✉️</span> Email
          </a>
        </div>
        <p className="foot-note">
          Your feedback is anonymous. If you include contact info, we’ll only
          use it to follow up on this submission.
        </p>
      </footer>
    </div>
  );
}
